<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowUpDown, Plus, ChevronRight, Users } from 'lucide-vue-next'
import { AppButton, EmptyState, GradePill, mix, type PaletteHue } from '@/design-system/components'
import type { Student } from '@/api/api'
import Monogram from '@/components/classes/Monogram.vue'
import CreateStudentModal from '@/components/modals/CreateStudentModal.vue'

const props = defineProps<{
  classId: string
  hue: PaletteHue
  students: Student[]
  finalPctById: Record<string, number | null>
}>()
const emit = defineEmits<{ changed: [] }>()

const router = useRouter()
const sortKey = ref<'name' | 'id'>('name')
const headBand = computed(() => mix(props.hue.solid, 0.1))

const sorted = computed(() => {
  const list = [...props.students]
  if (sortKey.value === 'id') {
    return list.sort((a, b) => (a.schoolId ?? '').localeCompare(b.schoolId ?? '', undefined, { numeric: true }))
  }
  return list.sort((a, b) =>
    (a.lastName ?? '').localeCompare(b.lastName ?? '') ||
    (a.firstName ?? '').localeCompare(b.firstName ?? ''),
  )
})

function genderLetter(g?: string): string | null {
  if (!g) return null
  const u = g.trim().toUpperCase()
  if (u.startsWith('F')) return 'F'
  if (u.startsWith('M')) return 'M'
  return u[0] ?? null
}

const showAdd = ref(false)
function onCreated() {
  emit('changed')
}
</script>

<template>
  <div>
    <div class="stu__toolbar">
      <span class="stu__eyebrow">{{ students.length }} students</span>
      <div class="stu__tools">
        <AppButton variant="ghost" size="sm" @click="sortKey = sortKey === 'name' ? 'id' : 'name'">
          <ArrowUpDown :size="15" /> {{ sortKey === 'name' ? 'Name' : 'ID' }}
        </AppButton>
        <AppButton variant="secondary" size="sm" @click="showAdd = true">
          <Plus :size="14" /> Add student
        </AppButton>
      </div>
    </div>

    <EmptyState
      v-if="!students.length"
      :icon="Users"
      :hue="hue"
      :cta-icon="Plus"
      title="No students yet"
      body="Add students to this class to start taking the register, marking assignments and writing reports."
      cta="Add student"
      @action="showAdd = true"
    />
    <div v-else class="stu__table">
      <div class="stu__head" :style="{ background: headBand }">
        <div class="stu__hcell">Student</div>
        <div class="stu__hcell">ID</div>
        <div class="stu__hcell stu__hcell--center">Gender</div>
        <div class="stu__hcell stu__hcell--right">Grade</div>
        <div class="stu__hcell" />
      </div>
      <button
        v-for="s in sorted"
        :key="s.id"
        type="button"
        class="stu__row"
        @click="router.push(`/classes/${classId}/student/${s.id}`)"
      >
        <div class="stu__cell stu__cell--student">
          <Monogram :first="s.firstName" :last="s.lastName" :key-id="s.id" :photo-url="s.photoUrl" :size="36" />
          <span class="stu__name"><b>{{ s.lastName }}</b>, {{ s.firstName }}</span>
        </div>
        <div class="stu__cell stu__id">{{ s.schoolId || '—' }}</div>
        <div class="stu__cell stu__cell--center">
          <span v-if="genderLetter(s.gender)" class="stu__gender">{{ genderLetter(s.gender) }}</span>
          <span v-else class="stu__dash">—</span>
        </div>
        <div class="stu__cell stu__cell--right">
          <GradePill :pct="s.id ? (finalPctById[s.id] ?? null) : null" />
        </div>
        <div class="stu__cell stu__cell--center">
          <ChevronRight :size="15" class="stu__chev" />
        </div>
      </button>
    </div>

    <CreateStudentModal v-model="showAdd" :class-id="classId" @created="onCreated" />
  </div>
</template>

<style scoped>
.stu__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.stu__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.stu__tools {
  display: flex;
  gap: 6px;
}
.stu__table {
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  background: var(--surface);
}
.stu__head,
.stu__row {
  display: grid;
  grid-template-columns: 1fr 130px 80px 110px 44px;
  align-items: center;
}
.stu__row {
  width: 100%;
  border: 0;
  border-top: 1px solid var(--hairline);
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
  transition: background var(--motion-fast);
}
.stu__row:hover {
  background: var(--ink-50);
}
.stu__hcell {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-500);
}
.stu__hcell--center {
  text-align: center;
}
.stu__hcell--right {
  text-align: right;
}
.stu__cell {
  padding: 0 16px;
}
.stu__cell--student {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  min-width: 0;
}
.stu__cell--center {
  display: flex;
  justify-content: center;
}
.stu__cell--right {
  display: flex;
  justify-content: flex-end;
}
.stu__name {
  font-size: 14.5px;
  color: var(--ink-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stu__name b {
  font-weight: 600;
  color: var(--text-primary);
}
.stu__id {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: var(--ink-500);
}
.stu__gender {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: var(--radius-xs);
  background: var(--palette-indigo-soft);
  color: var(--palette-indigo-ink);
}
.stu__dash {
  color: var(--ink-300);
}
.stu__chev {
  color: var(--ink-300);
}
</style>
